import React, { memo, useEffect, useMemo } from "react";
import {
	useTable,
	usePagination,
	useRowState,
	useSortBy,
	useRowSelect,
	Column,
	SortingRule,
} from "react-table";
import fp from "lodash/fp";
import clsx from "clsx";

import { WithLoader } from "../skeleton/withLoader";

import { TableHeader } from "./header";
import { TableBody } from "./body";
import { TableFooter } from "./footer";

import styles from "./table.module.scss";
import { SortBy } from "types";
import { usePrevious } from "hooks";

const PAGE_SIZE = 50;

const defaultProps = {
	meta: { loading: false, totalItems: 0 },
	initialState: {},
	defaultColumn: {},
	hideFooter: false,
	stickyFooter: false,
	stickyHeader: false,
	compact: false,
};

type Props = {
	columns: Array<Column>;

	handlePageChange?: Function;
	handleSortChange?: Function;
	handleRowSelect?: Function;
	onRowClick?: (row: { [key: string]: any }) => any;
	handleRowSubmit?: ({
		values,
		row,
		setRowState,
	}: {
		values: { [key: string]: any };
		row: { [key: string]: any };
		setRowState: any;
	}) => any;

	data: Array<{}>;
	customStyle?: Object;
	meta?: typeof defaultProps.meta;

	className?: string;
	isTotalCount?: boolean;
	initialState?: {
		pageIndex?: number;
		pageSize?: number;
		sortBy?: Array<{ id: string; desc: boolean }>;
	};
	defaultColumn?: { sortDescFirst?: boolean };
	hideFooter?: typeof defaultProps.hideFooter;
	stickyFooter?: typeof defaultProps.stickyFooter;
	stickyHeader?: typeof defaultProps.stickyHeader;
	compact?: typeof defaultProps.compact;
};

// 1. Before and after any update sync code between projects
// Currently it's "RiseCRM.PortalClient" and "Web Merchant"

// 2. Fills parent. To limit height set height of the parent

// 3. Some optimiations might be needed. Along with extensions
const Table = ({
	columns,

	handlePageChange,
	handleSortChange,
	handleRowSelect,
	onRowClick,
	handleRowSubmit,
	data,
	meta: { loading, totalItems } = defaultProps.meta,

	className,
	customStyle,
	initialState,
	defaultColumn,
	hideFooter = false,
	stickyFooter = false,
	stickyHeader = false,
	compact = false,
}: Props) => {
	const pageCount = useMemo(() => Math.ceil(totalItems / PAGE_SIZE) || 1, [
		totalItems,
	]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		rows,
		page,
		gotoPage,
		setRowState,
		state: { pageIndex, sortBy, selectedRowIds },
	} = useTable(
		{
			columns,
			data,

			initialState: {
				pageIndex: 0,
				pageSize: PAGE_SIZE,
				...initialState, // Be cearful as this might override your state when data changes
			},
			defaultColumn: {
				sortDescFirst: true, // That's the way all tables are currently setting order.
				...defaultColumn,
			},

			manualSortBy: true,
			disableSortRemove: true, // Sort must me selected on one of the columns
			disableMultiSort: true, // Doesn't exist on the api

			manualPagination: true,
			pageCount: pageCount,
		},
		useRowState,
		useSortBy,
		usePagination,
		useRowSelect,
	);

	const prevInitState = usePrevious<{
		initialState?: {
			pageIndex?: number | undefined;
			pageSize?: number | undefined;
			sortBy?: SortBy[] | undefined;
		};
		sortBy: SortingRule<{}>[];
		pageIndex: number;
	}>({
		initialState,
		sortBy,
		pageIndex,
	});

	useEffect(() => {
		if (
			handlePageChange &&
			!fp.isEqual(pageIndex, fp.get("pageIndex", initialState))
		) {
			if (prevInitState?.pageIndex !== pageIndex) {
				handlePageChange({ pageIndex, pageSize: PAGE_SIZE });
				gotoPage(pageIndex);
			} else if (
				prevInitState?.initialState?.pageIndex !==
				initialState?.pageIndex
			) {
				handlePageChange({
					pageIndex: initialState?.pageIndex,
					pageSize: PAGE_SIZE,
				});
				gotoPage(initialState?.pageIndex || 0);
			}
		}
	}, [handlePageChange, pageIndex, initialState, gotoPage, prevInitState]);

	useEffect(() => {
		if (
			handleSortChange &&
			!fp.isEqual(sortBy, fp.get("sortBy", initialState)) // Initial state triggers that action
		) {
			if (
				prevInitState?.initialState === initialState &&
				prevInitState?.sortBy !== sortBy
			) {
				handleSortChange({ sortBy });
			}
		}
	}, [handleSortChange, sortBy, initialState, prevInitState]);

	useEffect(() => {
		if (handleRowSelect) {
			handleRowSelect(rows, selectedRowIds);
		}
	}, [rows, handleRowSelect, selectedRowIds]);

	const clickableRowClass =
		onRowClick && fp.isFunction(onRowClick) && styles.clickableRow;
	const stickyFooterClass = stickyFooter && styles.stickyFooter;
	const stickyHeaderClass = stickyHeader && styles.stickyHeader;
	const compactTableClass = compact && styles.compact;

	return (
		<div
			{...getTableProps()}
			className={clsx(
				styles.table,
				className,
				clickableRowClass,
				stickyFooterClass,
				stickyHeaderClass,
				compactTableClass,
			)}
			style={customStyle}
		>
			<WithLoader variant="table" loading={loading}>
				<TableHeader header={headerGroups[0]} />
				<TableBody
					setRowState={setRowState}
					getTableBodyProps={getTableBodyProps}
					prepareRow={prepareRow}
					onRowClick={onRowClick}
					handleRowSubmit={handleRowSubmit}
					rows={page}
				/>
				{!hideFooter && (
					<TableFooter
						pageIndex={pageIndex}
						totalItems={totalItems}
						gotoPage={gotoPage}
						pageSize={PAGE_SIZE}
					/>
				)}
			</WithLoader>
		</div>
	);
};

export default memo(Table);
